import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Header from '../../components/Header';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import axios from 'axios';
import { 
  Package, Plus, DollarSign, ShoppingBag, ArrowUpRight, 
  ArrowDownLeft, Loader2, Wallet, Edit
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  active: 'bg-green-100 text-green-700',
  sold: 'bg-blue-100 text-blue-700',
  removed: 'bg-gray-100 text-gray-700',
  pending: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-green-100 text-green-700'
};

export default function MyListings() {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  
  const [products, setProducts] = useState([]);
  const [wallet, setWallet] = useState({ balance: 0, transactions: [] });
  const [purchases, setPurchases] = useState({ purchases: [], sales: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [productsRes, walletRes, purchasesRes] = await Promise.all([
        axios.get(`${API}/marketplace/my-products`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/marketplace/wallet`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/marketplace/purchases`, { headers: getAuthHeaders(), withCredentials: true })
      ]);
      
      setProducts(productsRes.data);
      setWallet(walletRes.data);
      setPurchases(purchasesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="my-listings-page">
      <Header />
      
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 font-['Chivo']">My Marketplace</h1>
            <p className="text-gray-600 mt-1">Manage your listings and view transactions</p>
          </div>
          <Link to="/marketplace/sell">
            <Button className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              New Listing
            </Button>
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card data-testid="wallet-card">
            <CardContent className="py-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Wallet className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Wallet Balance</p>
                  <p className="text-2xl font-bold text-gray-900">${wallet.balance.toFixed(2)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="listings-stat">
            <CardContent className="py-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Package className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Active Listings</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {products.filter(p => p.status === 'active').length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="sales-stat">
            <CardContent className="py-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <ShoppingBag className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Sales</p>
                  <p className="text-2xl font-bold text-gray-900">{purchases.sales.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
          </div>
        ) : (
          <Tabs defaultValue="listings" className="space-y-6">
            <TabsList>
              <TabsTrigger value="listings" data-testid="tab-listings">
                My Listings ({products.length})
              </TabsTrigger>
              <TabsTrigger value="purchases" data-testid="tab-purchases">
                Purchases ({purchases.purchases.length})
              </TabsTrigger>
              <TabsTrigger value="sales" data-testid="tab-sales">
                Sales ({purchases.sales.length})
              </TabsTrigger>
              <TabsTrigger value="transactions" data-testid="tab-transactions">
                Transactions
              </TabsTrigger>
            </TabsList>

            {/* Listings Tab */}
            <TabsContent value="listings">
              {products.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No Listings Yet</h3>
                    <p className="text-gray-600 mb-6">Start selling by creating your first listing</p>
                    <Link to="/marketplace/sell">
                      <Button className="bg-blue-600 hover:bg-blue-700">
                        <Plus className="w-4 h-4 mr-2" />
                        Create Listing
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {products.map((product) => (
                    <Card key={product.product_id} className="overflow-hidden" data-testid={`listing-${product.product_id}`}>
                      <div className="h-40 bg-gray-100 flex items-center justify-center">
                        {product.images && product.images.length > 0 ? (
                          <img src={product.images[0]} alt={product.title} className="w-full h-full object-cover" />
                        ) : (
                          <Package className="w-12 h-12 text-gray-400" />
                        )}
                      </div>
                      <CardContent className="py-4">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-semibold text-gray-900 line-clamp-1">{product.title}</h3>
                          <span className={`text-xs px-2 py-1 rounded-full ${statusColors[product.status]}`}>
                            {product.status.toUpperCase()}
                          </span>
                        </div>
                        <p className="text-xl font-bold text-blue-600 mb-3">${product.price.toFixed(2)}</p>
                        <Link to={`/marketplace/product/${product.product_id}`}>
                          <Button variant="outline" className="w-full" size="sm">
                            <Edit className="w-4 h-4 mr-2" />
                            View / Edit
                          </Button>
                        </Link>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Purchases Tab */}
            <TabsContent value="purchases">
              {purchases.purchases.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No Purchases</h3>
                    <p className="text-gray-600">Items you buy will appear here</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {purchases.purchases.map((purchase) => (
                    <Card key={purchase.purchase_id}>
                      <CardContent className="py-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                              <ArrowDownLeft className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{purchase.product_title}</p>
                              <p className="text-sm text-gray-500">from {purchase.seller_name}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-gray-900">${purchase.product_price.toFixed(2)}</p>
                            <span className={`text-xs px-2 py-1 rounded-full ${statusColors[purchase.status]}`}>
                              {purchase.status.toUpperCase()}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Sales Tab */}
            <TabsContent value="sales">
              {purchases.sales.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <DollarSign className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No Sales Yet</h3>
                    <p className="text-gray-600">Your sales will appear here</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {purchases.sales.map((sale) => (
                    <Card key={sale.purchase_id}>
                      <CardContent className="py-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                              <ArrowUpRight className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{sale.product_title}</p>
                              <p className="text-sm text-gray-500">to {sale.buyer_name}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-green-600">+${sale.seller_receives.toFixed(2)}</p>
                            <p className="text-xs text-gray-500">Fee: ${sale.platform_fee.toFixed(2)}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Transactions Tab */}
            <TabsContent value="transactions">
              {wallet.transactions?.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Wallet className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No Transactions</h3>
                    <p className="text-gray-600">Your wallet transactions will appear here</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {(wallet.transactions || []).map((tx, idx) => (
                    <Card key={tx.transaction_id || tx._id || `${tx.type}-${tx.created_at || idx}`}>
                      <CardContent className="py-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              tx.type === 'sale' ? 'bg-green-100' : 'bg-red-100'
                            }`}>
                              {tx.type === 'sale' ? (
                                <ArrowDownLeft className="w-5 h-5 text-green-600" />
                              ) : (
                                <ArrowUpRight className="w-5 h-5 text-red-600" />
                              )}
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900 capitalize">{tx.type}</p>
                              <p className="text-sm text-gray-500">{tx.product_title}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`font-bold ${tx.type === 'sale' ? 'text-green-600' : 'text-red-600'}`}>
                              {tx.type === 'sale' ? '+' : '-'}${tx.amount.toFixed(2)}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(tx.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
}
